import * as React from 'react';
import { ChevronRight, Layers } from 'lucide-react';
import type { SpaceWithSiteCount } from '@klareospace/pharos-lib-types';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SpaceResultProps {
  onBrowseSpace: (spaceId: string, spaceName: string) => void;
  onClose: () => void;
  space: SpaceWithSiteCount;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SpaceResult({ onBrowseSpace, onClose, space }: SpaceResultProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { selectSpace, spaceId: currentSpaceId } = useMasterContext();
  const isActive = space.id === currentSpaceId;
  const siteLabel = space.siteCount === 1 ? t('mainSelector.site') : t('mainSelector.sitesCount');

  const handleSelect = (): void => {
    selectSpace(space.id);
    onClose();
  };

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    handleSelect();
  };

  const handleBrowseClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onBrowseSpace(space.id, space.name);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2.5 py-2 mx-1 w-[calc(100%-0.5rem)] rounded-md transition-colors cursor-pointer group/space-result',
        isActive ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={handleSelect}
    >
      <Layers className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="flex-1 text-xs truncate">{space.name}</span>
      {isActive ? (
        <span className="text-[9px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
          {t('mainSelector.current')}
        </span>
      ) : (
        <button className="text-[11px] shrink-0" onClick={handleSelectClick} type="button">
          <span className="text-muted-foreground group-hover/space-result:hidden">
            {space.siteCount} {siteLabel}
          </span>
          <span className="text-primary font-medium hidden group-hover/space-result:inline">
            {t('mainSelector.select')}
          </span>
        </button>
      )}
      <button
        aria-label={`Browse sites in ${space.name}`}
        className="p-0.5 rounded hover:bg-accent transition-colors shrink-0"
        onClick={handleBrowseClick}
        type="button"
      >
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
